// SiteMeterCard — Phase 3 of the Site Overview redesign (djin-cloud#67).
//
// Row 3 left panel on the dashboard grid (reordered per #273; was row 4 right
// pre-2026-05-11). Single-meter collapse of the site_summary meter_* fields.
// Shows grid frequency, power factor, import/export direction (derived from
// sign of meter_active_power_kw), |active power|, and three per-phase voltage
// tiles.
//
// Design contract: docs/plans/2026-04-20-site-overview-redesign-design.md §4.4.
// Mockup: docs/plans/2026-04-20-site-overview-mockup.html (row 4 right in mockup; row 3 left after #273).
//
// TODO(multi-meter): fan out to meters[] when a multi-meter site appears —
// see design §9.1. Until then, the backend collapses to a single meter and
// exposes meter_power_factor / meter_voltage_{a,b,c}_v / meter_active_power_kw
// on the site_summary wire payload.
//
// WHICH meter (djin-cloud#889): the site's configured
// kpi_metadata.power_summary_source when that meter reported in the batch,
// else the first by device_id ASC. This card and the LIVE POWER SUMMARY above
// it therefore report the SAME device — before #889 the summary followed
// power_summary_source while this card always took the alphabetically first
// meter, so a multi-meter site showed two different meters side by side with
// nothing on screen saying so.
//
// Voltage display honors issue #59: V below 2000, kV above. `formatVoltage`
// lives in web/src/utils/formatVoltage.ts.
//
// Null-safety: every field renders "—" when its KPI is null. No fabricated
// defaults (workspace rule [No fabricated fallback data]).

import { ArrowDownLeft, ArrowUpRight, Minus } from 'lucide-react';
import { formatVoltage } from '../../../utils/formatVoltage';

export interface SiteMeterCardProps {
  /** Live site_summary KPI values from the WebSocket telemetry:batch payload. */
  kpiValues: Record<string, number | null>;
}

type Direction = 'IMPORT' | 'EXPORT' | 'IDLE' | '—';

function deriveDirection(activePowerKw: number | null): Direction {
  if (activePowerKw == null) return '—';
  if (activePowerKw > 0) return 'IMPORT';
  if (activePowerKw < 0) return 'EXPORT';
  return 'IDLE';
}

function DirectionIcon({ dir }: { dir: Direction }) {
  if (dir === 'IMPORT') return <ArrowDownLeft className="w-3.5 h-3.5 text-ems-green" aria-hidden="true" />;
  if (dir === 'EXPORT') return <ArrowUpRight className="w-3.5 h-3.5 text-ems-blue" aria-hidden="true" />;
  if (dir === 'IDLE') return <Minus className="w-3.5 h-3.5 text-ems-text-muted" aria-hidden="true" />;
  return null;
}

function formatNumber(val: number | null, fractionDigits = 1): string {
  if (val == null) return '—';
  return val.toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
}

function formatPowerFactor(val: number | null): string {
  if (val == null) return '—';
  return val.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

interface TileProps {
  label: string;
  testId?: string;
  value: string;
  unit?: string;
  valueClassName?: string;
}

function Tile({ label, testId, value, unit, valueClassName }: TileProps) {
  return (
    <div className="rounded-lg p-3 bg-ems-bg-card/40 border border-ems-border">
      <div className="text-[10px] text-ems-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-baseline gap-1">
        <span
          data-testid={testId}
          className={`text-xl font-mono font-bold tabular-nums ${valueClassName ?? 'text-ems-text-primary'}`}
        >
          {value}
        </span>
        {unit && <span className="text-xs text-ems-text-muted">{unit}</span>}
      </div>
    </div>
  );
}

export function SiteMeterCard({ kpiValues }: SiteMeterCardProps) {
  const freq = kpiValues.grid_frequency_hz ?? null;
  const pf = kpiValues.meter_power_factor ?? null;
  const va = kpiValues.meter_voltage_a_v ?? null;
  const vb = kpiValues.meter_voltage_b_v ?? null;
  const vc = kpiValues.meter_voltage_c_v ?? null;
  const p = kpiValues.meter_active_power_kw ?? null;

  const direction = deriveDirection(p);
  // Active-power display is unsigned (direction tile carries the sign). Null → "—".
  const activeDisplay = p == null ? '—' : formatNumber(Math.abs(p));

  return (
    <section className="card rounded-xl border border-ems-border-bright/50 overflow-hidden bg-ems-bg-card/60 h-full flex flex-col">
      <div className="px-5 py-3 flex items-center gap-2 border-b border-ems-border">
        <h3 className="text-sm font-semibold text-ems-text-primary tracking-wide uppercase">
          Site Meter
        </h3>
        <div className="ml-auto flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full bg-ems-green animate-ems-pulse"
            aria-hidden="true"
          />
          <span className="text-[10px] text-ems-text-muted uppercase tracking-wider">Live</span>
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Top row: grid freq, PF, direction */}
        <div className="grid grid-cols-3 gap-3">
          <Tile
            label="Grid Frequency"
            testId="meter-grid-freq-value"
            value={formatNumber(freq, 2)}
            unit="Hz"
          />
          <Tile
            label="Power Factor"
            testId="meter-power-factor-value"
            value={formatPowerFactor(pf)}
            valueClassName="text-ems-cyan"
          />
          <div className="rounded-lg p-3 bg-ems-bg-card/40 border border-ems-border">
            <div className="text-[10px] text-ems-text-muted uppercase tracking-wider mb-1">Direction</div>
            <div className="flex items-center gap-1.5">
              <DirectionIcon dir={direction} />
              <span
                data-testid="meter-direction"
                className={`text-xl font-mono font-bold tabular-nums ${
                  direction === 'IMPORT'
                    ? 'text-ems-green'
                    : direction === 'EXPORT'
                      ? 'text-ems-blue'
                      : 'text-ems-text-primary'
                }`}
              >
                {direction}
              </span>
            </div>
          </div>
        </div>

        {/* Active power emphasized */}
        <div className="rounded-lg p-3 bg-ems-bg-card/40 border border-ems-border-bright/40">
          <div className="text-[10px] text-ems-text-muted uppercase tracking-wider mb-1">Active Power</div>
          <div className="flex items-baseline gap-1">
            <span
              data-testid="meter-active-power-value"
              className="text-3xl font-mono font-bold tabular-nums text-ems-text-primary"
            >
              {activeDisplay}
            </span>
            {p != null && <span className="text-sm text-ems-text-muted">kW</span>}
          </div>
        </div>

        {/* Per-phase voltage */}
        <div className="grid grid-cols-3 gap-3">
          <Tile
            label="Voltage A"
            testId="meter-voltage-a-value"
            value={formatVoltage(va, 'ac')}
          />
          <Tile
            label="Voltage B"
            testId="meter-voltage-b-value"
            value={formatVoltage(vb, 'ac')}
          />
          <Tile
            label="Voltage C"
            testId="meter-voltage-c-value"
            value={formatVoltage(vc, 'ac')}
          />
        </div>
      </div>
    </section>
  );
}

export default SiteMeterCard;
